import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { uploadEngine, type UploadProgress } from "@/lib/uploads/engine";
import { uploadQueue, type QueuedUpload } from "@/lib/uploads/queue";
import { NotAuthenticatedError } from "@/lib/uploads/errors";
import { toast } from "sonner";

interface UploadContextValue {
  uploads: UploadProgress[];
  enqueue: (file: File, folderId: string | null) => Promise<string | null>;
  pause: (id: string) => Promise<void>;
  resume: (id: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  clearCompleted: () => Promise<void>;
  hasActive: boolean;
  pendingResume: QueuedUpload[];
  resumeAll: () => Promise<void>;
  discardAll: () => Promise<void>;
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [pendingResume, setPendingResume] = useState<QueuedUpload[]>([]);

  const refreshPendingResume = useCallback(async (id: string | null) => {
    if (!id) {
      setPendingResume([]);
      return;
    }
    try {
      const list = await uploadQueue.listResumableForUser(id);
      // Only surface genuinely resumable rows. `listResumableForUser` already
      // excludes `uploading` and `failed`, but we double-guard here so a future
      // change can't accidentally re-show ghost banners on every refresh.
      const filtered = list.filter((u) => u.status === "queued" || u.status === "paused");
      setPendingResume(filtered);
    } catch {
      setPendingResume([]);
    }
  }, []);

  // Track auth and bind to engine
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const id = data.session?.user?.id ?? null;
      setUserId(id);
      uploadEngine.setCurrentUser(id);
      refreshPendingResume(id);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const id = session?.user?.id ?? null;
      setUserId(id);
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") {
        uploadEngine.setCurrentUser(id);
        if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && id) {
          refreshPendingResume(id);
        }
      } else if (event === "SIGNED_OUT") {
        uploadEngine.setCurrentUser(null);
        setPendingResume([]);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [refreshPendingResume]);

  // Subscribe to engine progress
  useEffect(() => {
    const unsub = uploadEngine.onProgress((map) => {
      setUploads(Array.from(map.values()).sort((a, b) => a.fileName.localeCompare(b.fileName)));
    });
    return unsub;
  }, []);

  // Auto-clear the resume banner once the user has acted (those uploads are now active or gone)
  useEffect(() => {
    if (pendingResume.length === 0) return;
    const activeIds = new Set(uploads.filter((u) => u.status === "uploading" || u.status === "completed").map((u) => u.localUploadId));
    const remaining = pendingResume.filter((p) => !activeIds.has(p.localUploadId));
    if (remaining.length !== pendingResume.length) setPendingResume(remaining);
  }, [uploads, pendingResume]);

  // Lifecycle toasts (uploaded/duplicate/failed/offline) are emitted by the
  // engine via the batched `uploadToast` helper. We only surface the "Waiting
  // for sign-in" hint here, which is engine-state-derived and rare.
  useEffect(() => {
    const lastSeen = new Map<string, string>();
    const unsub = uploadEngine.onProgress((map) => {
      for (const [id, p] of map) {
        const prev = lastSeen.get(id);
        if (prev !== p.status) {
          if (p.status === "paused" && p.error === "Waiting for sign-in") {
            toast.warning(`Waiting for sign-in to continue upload`);
          } else if (p.status === "uploading" && prev === "paused") {
            // Single-file resume notice; rare enough not to need batching.
            toast.info(`Upload resumed: ${p.fileName}`);
          }
          lastSeen.set(id, p.status);
        }
      }
    });
    return unsub;
  }, []);

  const enqueue = useCallback(
    async (file: File, folderId: string | null) => {
      if (!userId) {
        // Surface a typed error so callers can react (e.g. show a sign-in prompt).
        const err = new NotAuthenticatedError();
        toast.error(err.message);
        throw err;
      }
      return await uploadEngine.enqueue(file, folderId, userId);
    },
    [userId],
  );

  const resumeAll = useCallback(async () => {
    const list = pendingResume;
    setPendingResume([]);
    for (const p of list) {
      try { await uploadEngine.resume(p.localUploadId); } catch (e) { console.warn(e); }
    }
  }, [pendingResume]);

  const discardAll = useCallback(async () => {
    const list = pendingResume;
    setPendingResume([]);
    for (const p of list) {
      try { await uploadEngine.cancel(p.localUploadId); } catch (e) { console.warn(e); }
    }
  }, [pendingResume]);

  const value = useMemo<UploadContextValue>(() => ({
    uploads,
    enqueue,
    pause: (id) => uploadEngine.pause(id),
    resume: (id) => uploadEngine.resume(id),
    cancel: (id) => uploadEngine.cancel(id),
    clearCompleted: () => uploadEngine.clearCompleted(),
    hasActive: uploads.some((u) => u.status === "uploading" || u.status === "queued" || u.status === "paused" || u.status === "failed"),
    pendingResume,
    resumeAll,
    discardAll,
  }), [uploads, enqueue, pendingResume, resumeAll, discardAll]);

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploads(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    return {
      uploads: [],
      enqueue: async () => null,
      pause: async () => {},
      resume: async () => {},
      cancel: async () => {},
      clearCompleted: async () => {},
      hasActive: false,
      pendingResume: [],
      resumeAll: async () => {},
      discardAll: async () => {},
    };
  }
  return ctx;
}

export function useUploadsForFolder(folderId: string | null) {
  const { uploads } = useUploads();
  return useMemo(
    () => uploads.filter((u) => (u.folderId ?? null) === (folderId ?? null)),
    [uploads, folderId],
  );
}

export function registerUploadCompletionListener(
  cb: (localId: string, folderId: string | null) => void,
): () => void {
  return uploadEngine.onCompleted(cb);
}
